// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { env } from '@/config/env';
import supabaseDirectApi from '@/utils/supabaseDirectApi';

// For debugging in development mode only
if (env.DEV) {
  console.log('Initializing Supabase client');
}

// Check if Supabase credentials are available
const hasSupabaseCredentials = !!(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY);

// Use hardcoded values if env variables are not available
const supabaseUrl = env.VITE_SUPABASE_URL || 'https://lcevfqxsypgtrquujsug.supabase.co';
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxjZXZmcXhzeXBndHJxdXVqc3VnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxNTY3OTAsImV4cCI6MjA1ODczMjc5MH0.j_PXFHgREbIFcR_AuPHzTFvf0HKM66jKoGq57JyP-yM';

if (!hasSupabaseCredentials && env.DEV) {
  console.warn('Supabase credentials not found in environment variables. Using hardcoded values.');
}

// Define the app domain - this ensures consistent redirect URLs
const APP_DOMAIN = env.VITE_APP_URL || 'https://www.coachvault.co.uk';

// Create the Supabase client with enhanced configuration for auth flows
let supabaseClient;

// Ensure the app URL has a trailing slash for consistency
const formattedAppUrl = APP_DOMAIN.endsWith('/') ? APP_DOMAIN : `${APP_DOMAIN}/`;

try {
  // Create a more robust client with auth options that support PKCE flow
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce', // Explicitly set flowType to PKCE for modern browsers
      debug: env.DEV
    },
    global: {
      headers: {
        'X-Client-Info': 'coach-vault-app',
        'X-Use-PKCE': 'true'
      }
      // Note: The redirect URL is set in the auth.signUp() options
    }
  });
  
  if (env.DEV) {
    console.log('Supabase client initialized with PKCE auth config');
  }
} catch (err) {
  console.error('Error initializing Supabase client with PKCE:', err);
  
  // Fall back to basic client if PKCE initialization fails
  try {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
    if (env.DEV) {
      console.log('Supabase client initialized with minimal config');
    }
  } catch (fallbackErr) {
    console.error('Error initializing minimal Supabase client:', fallbackErr);
    
    // Create a dummy client as a last resort to prevent crashes
    supabaseClient = {
      auth: {
        getSession: async () => ({ data: { session: null }, error: new Error('Auth client failed to initialize') }),
        signOut: async () => ({ error: new Error('Auth client failed to initialize') }),
        signInWithPassword: async () => ({ data: null, error: new Error('Auth client failed to initialize') }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        resetPasswordForEmail: async (email, options) => {
          console.error('Using fallback resetPasswordForEmail implementation');
          return { 
            data: null, 
            error: new Error('Auth client failed to initialize. Please contact support.')
          };
        },
        exchangeCodeForSession: async (code) => {
          console.error('Using fallback exchangeCodeForSession implementation');
          return {
            data: null,
            error: new Error('Auth client failed to initialize. Please contact support.')
          };
        }
      },
      functions: {
        setAuth: () => {}
      }
    };
    
    console.error('Created emergency fallback client to prevent crashes');
  }
}

// Export the created client
export const supabase = supabaseClient;

// Safely check if functions exist before calling
const safeSetAuth = () => {
  try {
    if (supabase && supabase.functions && typeof supabase.functions.setAuth === 'function') {
      supabase.functions.setAuth(supabaseAnonKey);
    }
  } catch (error) {
    console.error('Error setting function auth:', error);
  }
};

// Safely set function auth
safeSetAuth();

// Export a function to check and refresh the session if needed
export const refreshSession = async () => {
  try {
    if (!supabase.auth || typeof supabase.auth.refreshSession !== 'function') {
      console.error('refreshSession not available');
      return null;
    }
    
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      if (env.DEV) console.error('Session refresh error:', error);
      return null;
    }
    return data.session;
  } catch (error) {
    if (env.DEV) console.error('Error refreshing session:', error);
    return null;
  }
};

// Initialize auth and prepare secure session
const initializeAuth = async () => {
  try {
    // Check that getSession method exists
    if (!supabase.auth || typeof supabase.auth.getSession !== 'function') {
      console.error('Auth getSession method not available');
      return;
    }
    
    // Immediately check for an existing session
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      if (env.DEV) console.error('Error retrieving session:', error);
      return;
    }
    
    // Log minimal info for security (avoid logging tokens)
    if (env.DEV) {
      if (data.session) {
        console.log('Auth initialized with active session');
      } else {
        console.log('Auth initialized with no active session');
      }
    }
    
    // Check URL for auth-related parameters
    const checkForAuthParams = () => {
      try {
        // If we're in a browser environment
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          const hash = url.hash;
          const searchParams = new URLSearchParams(url.search);
          
          // Check for code parameter (PKCE flow)
          if (searchParams.has('code')) {
            console.log('Auth code parameter detected in URL');
            // Check if we need to redirect to reset-password
            if (!window.location.pathname.includes('reset-password')) {
              console.log('Code detected but not on reset-password page, redirecting...');
              window.location.href = `${APP_DOMAIN}/reset-password${url.search}`;
              return;
            }
          }
          
          // Check for token in hash (implicit flow)
          if (hash && (hash.includes('access_token=') || hash.includes('type=recovery'))) {
            console.log('Auth token detected in URL hash');
            // Check if we need to redirect to reset-password
            if (!window.location.pathname.includes('reset-password')) {
              console.log('Token detected but not on reset-password page, redirecting...');
              window.location.href = `${APP_DOMAIN}/reset-password${hash}`;
              return;
            }
          }
          
          // Check for error
          if (searchParams.has('error') || searchParams.has('error_description') ||
              hash.includes('error=') || hash.includes('error_description=')) {
            console.error('Auth error parameter detected in URL');
          }
        }
      } catch (err) {
        console.error('Error checking for auth parameters:', err);
      }
    };
    
    // Run the parameter check in all environments to ensure redirects work
    checkForAuthParams();
  } catch (err) {
    if (env.DEV) console.error('Error in initializeAuth:', err);
  }
};

// Safely set up auth state change listener
try {
  if (supabase.auth && typeof supabase.auth.onAuthStateChange === 'function') {
    supabase.auth.onAuthStateChange((event, session) => {
      // Log minimal info to avoid exposing tokens
      if (env.DEV) {
        console.log('Auth state changed:', event, session ? 'SESSION' : undefined);
      }
      
      // Process recovery flow for password reset if needed
      if (event === 'PASSWORD_RECOVERY') {
        if (env.DEV) console.log('Password recovery flow detected');
        
        // Use app domain for reset redirect
        const resetPasswordUrl = `${APP_DOMAIN}/reset-password`;
        
        // Ensure we're on the correct page for password reset
        if (!window.location.pathname.includes('reset-password')) {
          if (env.DEV) console.log('Redirecting to proper reset password page');
          
          // Check if we're already at root with a token
          const currentUrl = window.location.href;
          const hasToken = window.location.hash.includes('access_token=') || 
                          window.location.hash.includes('type=recovery') ||
                          window.location.search.includes('code=');
                          
          if (hasToken) {
            if (env.DEV) console.log('Found auth token, redirecting to reset-password page with token');
            
            // Preserve all parameters and properly redirect to reset-password page
            if (window.location.hash) {
              window.location.href = resetPasswordUrl + window.location.hash;
            } else if (window.location.search) {
              window.location.href = resetPasswordUrl + window.location.search;
            } else {
              window.location.href = resetPasswordUrl;
            }
            return;
          }
        }
      }
    });
  } else {
    console.error('Auth state change listener not available');
  }
} catch (listenerError) {
  console.error('Error setting up auth state change listener:', listenerError);
}

// Run auth initialization
initializeAuth().catch(err => {
  if (env.DEV) console.error('Auth initialization error:', err);
});

// Add token redirect handler to window for global access
if (typeof window !== 'undefined') {
  (window as any).fixPasswordResetToken = supabaseDirectApi.handleTokenRedirect;
}

// Only log in development mode
if (env.DEV) {
  console.log('Supabase client initialization complete');
  
  // Add a troubleshooter to the window object for debugging
  if (typeof window !== 'undefined') {
    (window as any).debugSupabase = {
      getSession: async () => {
        try {
          if (!supabase.auth || typeof supabase.auth.getSession !== 'function') {
            return { error: 'getSession method not available' };
          }
          
          const { data, error } = await supabase.auth.getSession();
          console.log('Current session:', data, error);
          return { data, error };
        } catch (e) {
          console.error('Error getting session:', e);
          return { error: e };
        }
      },
      resetPassword: async (email: string) => {
        try {
          // Use the direct API method to bypass potential SDK issues
          const resetUrl = `${APP_DOMAIN}/reset-password`;
          console.log('Sending reset to:', email, 'with redirect:', resetUrl);
          
          const result = await supabaseDirectApi.resetPassword(email, {
            redirectTo: resetUrl
          });
          
          console.log('Reset result:', result);
          return result;
        } catch (e) {
          console.error('Error resetting password:', e);
          return { error: e };
        }
      },
      getConfig: () => {
        return {
          url: supabaseUrl,
          appDomain: APP_DOMAIN,
          hasCredentials: hasSupabaseCredentials,
          authEnabled: !!(supabase.auth && typeof supabase.auth.getSession === 'function')
        };
      }
    };
  }
}

// Create a custom implementation of resetPasswordForEmail
// This replaces the internal Supabase function that's causing the flowType error
try {
  // Check if we can override the function
  if (supabase && supabase.auth) {
    // Create a direct replacement that doesn't rely on internal Supabase implementation
    const customResetPasswordForEmail = async (email: string, options: any = {}) => {
      try {
        console.log('[Custom Reset] Sending reset email to:', email);
        
        // Ensure redirectTo always uses our domain
        const resetPasswordUrl = `${APP_DOMAIN}/reset-password`;
        
        // Build request URL to Supabase auth API directly
        const resetUrl = `${supabaseUrl}/auth/v1/recover`;
        
        // Make the request directly to avoid internal flowType issues
        const response = await fetch(resetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'X-Client-Info': 'coach-vault-app'
          },
          body: JSON.stringify({
            email,
            redirect_to: resetPasswordUrl
          })
        });
        
        // Process response
        if (response.ok) {
          console.log('[Custom Reset] Password reset email sent successfully');
          return { data: {}, error: null };
        } else {
          const errorData = await response.json();
          console.error('[Custom Reset] Error response:', errorData);
          return { 
            data: null,
            error: {
              message: errorData.message || 'Failed to send password reset email',
              status: response.status
            }
          };
        }
      } catch (error) {
        console.error('[Custom Reset] Error sending reset email:', error);
        return { 
          data: null, 
          error: { 
            message: error instanceof Error ? error.message : 'Unknown error occurred',
            status: 500
          }
        };
      }
    };
    
    // Helper method to get the correct redirect URL for email verification
    const setupEmailVerification = async () => {
      try {
        // Get the app URL with proper formatting
        const appUrl = (env.VITE_APP_URL || 'https://www.coachvault.co.uk').replace(/\/$/, '');
        const redirectUrl = `${appUrl}/verify`;
        
        if (env.DEV) {
          console.log('Setting up email verification with redirect URL:', redirectUrl);
        }
        
        // Store flags in localStorage to track the verification flow
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('auth_flow', 'signup');
          localStorage.setItem('email_confirmation_pending', 'true');
          if (env.DEV) {
            console.log('[Email Verification] Set verification flags in localStorage');
          }
        }
        
        return redirectUrl;
      } catch (error) {
        console.error('Error setting up email verification:', error);
        return 'https://www.coachvault.co.uk/verify';
      }
    };
    
    // Add the helper to the window object for global access
    if (typeof window !== 'undefined') {
      (window as any).setupEmailVerification = setupEmailVerification;
    }
    
    // Override the resetPasswordForEmail function
    supabase.auth.resetPasswordForEmail = customResetPasswordForEmail;
    console.log('Successfully replaced resetPasswordForEmail with custom implementation');
  } else {
    console.error('Could not set custom auth functions - auth object not found');
  }
} catch (error) {
  console.error('Error setting up custom auth functions:', error);
}
